<template>
  <div class="y-datepicker">
    <y-text
      v-if="showLabel"
      class="title"
      font="Barlow, Barlow"
      color="#949494"
      size="14px"
    >
      {{ t("shared.hotel.search.checkInOut") }}
    </y-text>
    <div class="content-show" @click="handleOpenDate">
      <slot name="icon">
        <font-icon class="icon" icon="icon-rili" />
      </slot>
      <div class="content-show-label flex">
        <y-text
          class="text-date"
          font="var(--y-medium-font)"
          color="#292929"
          size="18px"
        >
          {{ dateShow }}
        </y-text>
        <y-text
          v-if="showNightCount"
          color="#696969"
          class="text-night"
          font="var(--y-medium-font)"
          size="18px"
        >
          {{ t("shared.hotel.search.night", nightCount) }}
        </y-text>
      </div>
    </div>
    <div class="select-content" v-if="showDatePick">
      <el-date-picker
        ref="datePickRef"
        :disabled-date="disabledDate"
        :cell-class-name="rateCellClassName"
        @change="handleChange"
        v-model="date"
        type="daterange"
        :placeholder="t('shared.hotel.search.checkInOutDate')"
        :first-day-of-week="0"
        popper-class="form-data-picker"
        @panel-change="handlePanelChange"
        :popper-append-to-body="false"
      >
        <template v-if="toggle" #default="cell">
          <div class="el-date-table-cell">
            <span class="el-date-table-cell__text">{{ cell.text }}</span>
            <span class="price" v-if="isHotelIdInRoute">{{
              ratesMap[dayjs(cell.date).format("YYYY-MM-DD")]
            }}</span>
          </div>
        </template>
      </el-date-picker>
    </div>
  </div>
</template>

<script setup>
import common from "@charmdeer/common";
import dayjs from "dayjs";
import { ElMessage } from "element-plus";
import { computed, nextTick, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";

import { getHotelRateCalendar } from "@/api/biz/openHotel";
import { useHotelStoreHook } from "@/distributions/consumer/y/store/hotel";
const route = useRoute();

// 价格日历（Y_RATE_CALENDAR）只在酒店详情页展示：详情页路由带 hotelId，酒店列表页没有
const isHotelIdInRoute = computed(() => {
  return !!route.query.hotelId;
});

const YHotelStore = useHotelStoreHook();
const { t } = useI18n();
const emit = defineEmits(["update:modelValue", "change"]);
const props = defineProps({
  showLabel: {
    type: Boolean,
    default: true
  },
  dateFormat: {
    type: String,
    default: "DD/MM/YYYY" // 默认保持原有格式
  },
  dateSeparator: {
    type: String,
    default: " - "
  },
  // 新增：是否显示天数
  showNightCount: {
    type: Boolean,
    default: true
  },
  // 新增：自定义日期格式化函数
  formatDateFn: {
    type: Function,
    default: null
  },
  modelValue: Object
});
const toggle = common.runtime.toggle.isActive("Y_RATE_CALENDAR");
const date = ref([props.modelValue.checkIn, props.modelValue.checkOut]);
const ratesMap = ref({});
const hotelId = ref("");
const datePickRef = ref(null);
const showDatePick = ref(false);
const dateShow = computed(() => {
  const _start = date.value[0];
  const _end = date.value[1];
  if (!_start || !_end) {
    return "";
  }

  // 如果父组件传入了自定义格式化函数，使用它
  if (props.formatDateFn && typeof props.formatDateFn === "function") {
    const formatted = props.formatDateFn(_start, _end);
    if (formatted) return formatted;
  }

  // 使用传入的日期格式
  const format = props.dateFormat;
  const separator = props.dateSeparator;

  // 如果日期格式包含时间，使用完整格式
  const startFormatted = dayjs(_start).format(format);
  const endFormatted = dayjs(_end).format(format);

  return `${startFormatted}${separator}${endFormatted}`;
});

watch(
  () => YHotelStore?.searchModel?.stay?.date,
  newVal => {
    date.value = [newVal?.checkIn, newVal?.checkOut];
  },
  {
    deep: true
  }
);

const nightCount = computed(() => {
  const _start = dayjs(date.value[0]);
  const _end = dayjs(date.value[1]);
  return _end.diff(_start, "day");
});

const handlePanelChange = data => {
  if (hotelId.value) {
    const dateOfLeft = data[0];
    const dateOfRight = data[1];
    const firstDayOfLeftMonth = dayjs(dateOfLeft).startOf("month").toDate();
    const lastDayOfRightMonth = dayjs(dateOfRight).endOf("month").toDate();
    fetchRateCalendar({ start: firstDayOfLeftMonth, end: lastDayOfRightMonth });
  }
};

const handleDateRange = dateRange => {
  const { start, end } = dateRange;
  if (start && end) {
    const startMonth = start.getMonth();
    const endMonth = end.getMonth();
    const lastDayOfStartMonth = new Date(
      start.getFullYear(),
      startMonth + 1,
      0
    );
    const lastDayOfEndMonth = new Date(end.getFullYear(), endMonth + 1, 0);
    let firstDayOfStartMonth = new Date(start.getFullYear(), startMonth, 1);
    const firstDayOfEndMonth = new Date(end.getFullYear(), endMonth, 1);

    const today = new Date();
    if (firstDayOfStartMonth < today) {
      firstDayOfStartMonth = today;
    }

    if (
      !Object.keys(ratesMap.value).includes(
        dayjs(lastDayOfStartMonth).format("YYYY-MM-DD")
      )
    ) {
      return { start: firstDayOfStartMonth, end: lastDayOfEndMonth };
    }
    if (
      !Object.keys(ratesMap.value).includes(
        dayjs(lastDayOfEndMonth).format("YYYY-MM-DD")
      )
    ) {
      return { start: firstDayOfEndMonth, end: lastDayOfEndMonth };
    }
  }
  return {};
};

const fetchRateCalendar = async dateRange => {
  const { start, end } = handleDateRange(dateRange);
  if (!start && !end) return;
  const newRatesMap = {};
  if (hotelId.value) {
    const res = await getHotelRateCalendar(
      // 服务端 booking/hotel/rateCalendar 的 dateRange.end 是开区间
      // （内部按 Array(end - start) 逐天取值，只覆盖 start..end-1），
      // 这里 +1 天，否则可见月份的最后一天没有价格
      { start: dayjs(start), end: dayjs(end).add(1, "day") },
      hotelId.value
    );
    res?.results?.forEach(result => {
      const price = result.lowest?.price;
      newRatesMap[result.checkIn] =
        price != null ? Math.floor(price).toLocaleString() : "";
    });
    ratesMap.value = {
      ...ratesMap.value,
      ...newRatesMap
    };
  } else {
    ratesMap.value = {};
  }
};

// 有价格的那天，单元格额外加一个 class，把日期行撑高以便放下价格
const rateCellClassName = date => {
  if (!toggle || !isHotelIdInRoute.value) {
    return "";
  }
  const price = ratesMap.value[dayjs(date).format("YYYY-MM-DD")];
  return price ? "rate-calendar-cell" : "";
};

// 进入 / 切换酒店时同步 hotelId 并拉取价格日历。
// 必须 immediate：详情页首屏挂载时 hotelId 已经写在 query 里，不是一次"路由变化"，
// 不 immediate 就永远不会触发拉取，详情页也就看不到价格。
watch(
  () => route.query.hotelId,
  newHotelId => {
    if (!newHotelId) {
      // 离开详情页（酒店列表页等）时清空，避免 keep-alive 残留上一家酒店的价格
      hotelId.value = "";
      ratesMap.value = {};
      return;
    }
    if (newHotelId === hotelId.value) {
      return;
    }
    hotelId.value = newHotelId;
    // 换酒店必须清空，否则会短暂显示上一家酒店的价格
    ratesMap.value = {};
    const start = new Date();
    const end = new Date(start.getFullYear(), start.getMonth() + 1, 1);
    fetchRateCalendar({ start: start, end: end });
  },
  { immediate: true }
);

const handleOpenDate = () => {
  showDatePick.value = true;
  nextTick(() => {
    datePickRef.value.focus(true);
  });
};

const handleChange = value => {
  if (dayjs(value[0]).isSame(dayjs(value[1]), "day")) {
    ElMessage.warning(t("shared.hotel.search.dateNotSame"));
    date.value = [
      dayjs(props.modelValue.checkIn).toDate(),
      dayjs(props.modelValue.checkOut).toDate()
    ];
    return;
  }

  showDatePick.value = false;
  emit("update:modelValue", { checkIn: value[0], checkOut: value[1] });
  emit("change", value);
};

const disabledDate = time => {
  return time.getTime() < Date.now() - 86400000;
};
</script>

<style lang="scss" scoped>
.y-datepicker {
  .title {
    margin-bottom: 8px;
    line-height: 1;
    display: block;
  }
  .content-show {
    display: flex;
    align-items: center;
    .icon {
      font-size: 18px;
    }
    .text-date {
      display: block;
      margin-left: 6px;
      margin-right: 16px;
    }
  }
  .select-content {
    display: flex;
    align-items: center;
  }
}

:deep(.el-range-editor.el-input__wrapper) {
  opacity: 0;
  z-index: -999;
  position: absolute;
}
</style>

<style>
/* 价格日历（Y_RATE_CALENDAR，仅酒店详情页）：日期下面一行显示当晚最低价 */
.el-date-table td.rate-calendar-cell {
  height: 50px;
}
.el-date-table td.rate-calendar-cell .el-date-table-cell {
  height: 42px;
}
.el-date-table td.rate-calendar-cell .el-date-table-cell .price {
  position: absolute;
  top: 31px;
  left: 50%;
  display: block;
  width: auto;
  height: 12px;
  margin: 0;
  font-size: 10px;
  line-height: 12px;
  white-space: nowrap;
  color: #949494;
  transform: translateX(-50%);
}
</style>
